import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import PopupBanner from '../../components/PopupBanner';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formType, setFormType] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormType('contact');
      setShowSuccess(false);
      alert('メッセージは500文字以内で入力してください');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4n8o61btbscp07qbai0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormType('contact');
        setShowSuccess(true);
        form.reset();
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get('email') as string;

    // Check if email is already registered in sessionStorage
    const registeredEmails = JSON.parse(sessionStorage.getItem('registeredNewsletterEmails') || '[]');
    if (registeredEmails.includes(email)) {
      alert('このメールアドレスは既に登録されています。');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4ohpr7vumd14qpjp4n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        // Save email to sessionStorage to prevent duplicate registration
        registeredEmails.push(email);
        sessionStorage.setItem('registeredNewsletterEmails', JSON.stringify(registeredEmails));
        
        setFormType('newsletter');
        setShowSuccess(true);
        form.reset();
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        alert('登録に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('登録に失敗しました。もう一度お試しください。');
    }
  };

  const handleMobileMenuClick = (targetId: string) => {
    setIsMobileMenuOpen(false);
    
    setTimeout(() => {
      const element = document.querySelector(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const toggleMobileMenu = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(prev => {
      console.log('Menu toggle:', !prev);
      return !prev;
    });
  };

  const properties = [
    { 
      code: 'SF-25', 
      region: '東京電力管内 群馬県伊勢崎市', 
      period: '工事負担金入金後7ヶ月程度(受給契約申込後15ヶ月)',
      date: '2025年10月6日',
      status: '発調契約申込済み。'
    },
    { 
      code: 'SF-33', 
      region: '東京電力管内 群馬県伊勢崎市', 
      period: '工事負担金入金後7ヶ月程度(受給契約申込後15ヶ月)',
      date: '2025年10月1日',
      status: '発調契約申込済み。'
    },
    { 
      code: 'BW-01', 
      region: '東京電力管内 埼玉県', 
      period: '工事負担金入金後7ヶ月程度(受給契約申込後15ヶ月)',
      date: '2025年12月8日',
      status: '発調契約申込済み。'
    }
  ];

  const quickConnectProperties = [
    { code: 'QC-01', period: '工事負担金入金後12カ月程度（受給契約申し込み後から6カ月程度）', status: '回答月/2025年10月23日' },
    { code: 'QC-02', period: '3ヶ月', status: '発調契約申込済み' },
    { code: 'QC-03', period: '6ヶ月', status: '発調契約申込済み' },
    { code: 'QC-04', period: '6ヶ月', status: '発調契約申込済み' },
    { code: 'QC-05', period: '3ヶ月', status: '発調契約申込済み' },
    { code: 'QC-06', period: '3ヶ月', status: '発調契約申込済み' },
    { code: 'QC-07', period: '6ヶ月', status: '発調契約申込済み' },
    { code: 'QC-08', period: '6ヶ月', status: '発調契約申込済み' }
  ];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "株式会社新藤不動産 JV 株式会社マック",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/429f3598ecc5059f29a02a26bec35937/17c45356e81e22a47455156ff6042e28.png",
          "width": "200",
          "height": "50"
        },
        "description": "系統用蓄電池事業の専門企業。東京電力管内に特化し、系統用蓄電池事業の候補地売却、資金調達サポート、LLP投資まで、系統用蓄電池事業のあらゆるニーズに対応。月間最大3,000万円の売上実績を誇る系統用蓄電池事業のリーディングカンパニーです。",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "伊勢崎市平和町24-28",
          "addressLocality": "伊勢崎市",
          "addressRegion": "群馬県",
          "postalCode": "372-0041",
          "addressCountry": "JP"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "36.3114",
          "longitude": "139.1959"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+81-270-25-8470",
          "contactType": "customer service",
          "email": "info.zaisan@xdesignsv.net",
          "availableLanguage": "Japanese",
          "areaServed": "JP"
        },
        "sameAs": [
          "https://www.instagram.com/shinjvmakc/"
        ],
        "knowsAbout": [
          "系統用蓄電池事業",
          "蓄電池投資",
          "再生可能エネルギー",
          "LLP投資",
          "東京電力管内",
          "エネルギー貯蔵システム"
        ]
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "系統用蓄電池事業専門 - 株式会社新藤不動産",
        "description": "系統用蓄電池事業の総合サポート。東京電力管内に特化した系統用蓄電池事業で、候補地売却から資金調達、LLP投資まで一貫してサポート。系統用蓄電池事業のことなら当社にお任せください。",
        "publisher": {
          "@id": `${siteUrl}/#organization`
        },
        "inLanguage": "ja",
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${siteUrl}/?s={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/#webpage`,
        "url": siteUrl,
        "name": "系統用蓄電池事業のご案内 | 株式会社新藤不動産 - 東京電力管内専門の系統用蓄電池事業",
        "isPartOf": {
          "@id": `${siteUrl}/#website`
        },
        "about": {
          "@id": `${siteUrl}/#organization`
        },
        "description": "系統用蓄電池事業の専門企業、株式会社新藤不動産。東京電力管内に特化した系統用蓄電池事業で月間最大3,000万円の売上実績。系統用蓄電池事業の候補地売却、資金調達サポート、LLP投資まで、系統用蓄電池事業のあらゆるニーズに対応。群馬県伊勢崎市を拠点に、系統用蓄電池事業への投資を全面サポートします。",
        "inLanguage": "ja",
        "breadcrumb": {
          "@id": `${siteUrl}/#breadcrumb`
        },
        "mainEntity": {
          "@id": `${siteUrl}/#service`
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${siteUrl}/#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "ホーム",
            "item": siteUrl
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "系統用蓄電池事業とは何ですか？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "系統用蓄電池事業とは、従来捨てていた電気や夜間等の安価な電力を蓄電池に貯蔵し、市場が必要とする時に高値で販売するビジネスモデルです。東京電力管内に特化した系統用蓄電池事業により、安定した収益を実現できます。系統用蓄電池事業は、容量市場・卸電力市場・需給調整市場の3つの市場から収益を得られるため、リスク分散された投資として注目されています。"
            }
          },
          {
            "@type": "Question",
            "name": "系統用蓄電池事業はなぜ東京電力管内に特化しているのですか？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "系統用蓄電池事業において、日本全国の電力会社の中で東京電力管内の事業所が最終的に優位であることを認識し、戦略的に東京電力管内5県に限定して申請しております。系統用蓄電池事業では、変電所空き容量情報の活用により、競争力のある接続回答を得ることができます。系統用蓄電池投資において、東京電力管内は最も有利な市場環境を提供します。"
            }
          },
          {
            "@type": "Question",
            "name": "系統用蓄電池事業の月間売上はどのくらいですか？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "系統用蓄電池事業の設備運転開始後の実績として、月間最大3,000万円の売上を達成しております。ただし、市場状況により変動する可能性があります。系統用蓄電池事業は、容量市場・卸電力市場・需給調整市場の3つの市場から収益を得られるため、安定した収益性が期待できます。系統用蓄電池事業への投資は、長期的な視点で安定収益を目指す方に最適です。"
            }
          },
          {
            "@type": "Question",
            "name": "系統用蓄電池事業のLLP投資のメリットは何ですか？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "系統用蓄電池事業のLLP（有限責任事業組合）投資は、1,100万円から参加可能で、出資額以上の損失が発生しない有限責任制度です。系統用蓄電池事業への投資において、リスクを分散しながら年間8〜12%の利回りを目指します。系統用蓄電池事業の運用管理は専門業者が実施し、投資家は収益を受け取るだけ。"
            }
          },
          {
            "@type": "Question",
            "name": "系統用蓄電池事業の資金調達のサポートはありますか？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "はい、系統用蓄電池事業の資金調達について、設備資材リース型、共同経営型、クラウドファンディング型など、複数の方法でサポートを提供しています。系統用蓄電池事業を始めたいお客様の状況に合わせた最適な資金調達方法をご提案いたします。系統用蓄電池事業の初期投資を抑えたい方から、大規模な系統用蓄電池事業を展開したい方まで、幅広くサポートいたします。"
            }
          }
        ]
      },
      {
        "@type": "Service",
        "@id": `${siteUrl}/#service`,
        "serviceType": "系統用蓄電池事業",
        "name": "系統用蓄電池事業総合サポート",
        "provider": {
          "@id": `${siteUrl}/#organization`
        },
        "areaServed": [
          {
            "@type": "State",
            "name": "東京電力管内"
          },
          {
            "@type": "City",
            "name": "伊勢崎市"
          },
          {
            "@type": "State",
            "name": "群馬県"
          },
          {
            "@type": "State",
            "name": "埼玉県"
          },
          {
            "@type": "State",
            "name": "茨城県"
          }
        ],
        "description": "系統用蓄電池事業の総合サポートサービス。安価な電力を蓄電池に貯蔵し、需要ピーク時に高値で販売する系統用蓄電池事業のビジネスモデル。東京電力管内に特化した戦略的な系統用蓄電池投資。月間最大3,000万円の売上実績を誇る系統用蓄電池事業のサポートを提供します。系統用蓄電池事業の候補地売却、資金調達、LLP投資まで一貫してサポート。",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "11000000",
            "priceCurrency": "JPY",
            "valueAddedTaxIncluded": false
          }
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "系統用蓄電池事業サービス一覧",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "系統用蓄電池事業候補地売却",
                "description": "東京電力管内の系統用蓄電池事業候補地の売却情報提供"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "系統用蓄電池事業資金調達サポート",
                "description": "系統用蓄電池事業開始に必要な資金調達の総合サポート"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "系統用蓄電池事業LLP投資",
                "description": "1,100万円から参加可能な系統用蓄電池事業。年間8〜12%の利回りを目指す系統用蓄電池投資。系統用蓄電池事業の運用管理は専門業者が実施し、投資家は収益を受け取るだけ。"
              }
            }
          ]
        }
      },
      {
        "@type": "LocalBusiness",
        "name": "株式会社新藤不動産",
        "image": "https://static.readdy.ai/image/429f3598ecc5059f29a02a26bec35937/17c45356e81e22a47455156ff6042e28.png",
        "@id": `${siteUrl}`,
        "url": siteUrl,
        "telephone": "+81-270-25-8470",
        "priceRange": "¥¥¥",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "伊勢崎市平和町24-28",
          "addressLocality": "伊勢崎市",
          "addressRegion": "群馬県",
          "postalCode": "372-0041",
          "addressCountry": "JP"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 36.3114,
          "longitude": 139.1959
        },
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday"
          ],
          "opens": "09:00",
          "closes": "18:00"
        },
        "slogan": "系統用蓄電池事業の総合サポート - 東京電力管内専門"
      },
      {
        "@type": "Product",
        "name": "系統用蓄電池事業投資プラン",
        "description": "東京電力管内に特化した系統用蓄電池事業への投資プラン。LLP（有限責任事業組合）を活用した共同投資モデルで、1,100万円から参加可能。年間8〜12%の利回りを目指す系統用蓄電池投資。系統用蓄電池事業の運用管理は専門業者が実施し、投資家は収益を受け取るだけ。",
        "brand": {
          "@type": "Brand",
          "name": "株式会社新藤不動産"
        },
        "offers": {
          "@type": "Offer",
          "url": siteUrl,
          "priceCurrency": "JPY",
          "price": "11000000",
          "priceValidUntil": "2025-12-31",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@id": `${siteUrl}/#organization`
          }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "81"
        },
        "category": "系統用蓄電池事業投資"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="系統用蓄電池事業のご案内 | 株式会社新藤不動産 - 東京電力管内専門の系統用蓄電池事業サポート"
        description="系統用蓄電池事業の専門企業、株式会社新藤不動産。東京電力管内に特化した系統用蓄電池事業で月間最大3,000万円の売上実績。系統用蓄電池事業の候補地売却、資金調達サポート、LLP投資まで、系統用蓄電池事業のあらゆるニーズに対応。群馬県伊勢崎市を拠点に、系統用蓄電池事業への投資を全面サポートします。"
        keywords="系統用蓄電池事業,系統用蓄電池,蓄電池投資,東京電力管内,グリッドビヨンド,エネルギー投資,LLP投資,伊勢崎市,群馬県,再生可能エネルギー,系統用蓄電池投資"
        canonical={siteUrl}
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Success Message */}
        {showSuccess && (
          <div className="fixed top-24 right-6 bg-teal-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center">
              <i className="ri-check-line text-2xl mr-3"></i>
              <span className="font-semibold">
                {formType === 'contact' ? 'お問い合わせを受け付けました' : '登録が完了しました'}
              </span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/429f3598ecc5059f29a02a26bec35937/17c45356e81e22a47455156ff6042e28.png"
                  alt="株式会社新藤不動産"
                  className="h-12"
                />
                <div className="ml-4 text-xl font-bold text-gray-900">株式会社新藤不動産</div>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="#home" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">ホーム</a>
                <Link to="/blog" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">ブログ</Link>
                <a href="#properties" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">売却案件</a>
                <a href="#financing" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">資金調達</a>
                <a href="#investment" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">事業案内</a>
                <a href="#contact" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">お問い合わせ</a>
              </div>
              <div 
                className="md:hidden cursor-pointer p-3 -mr-2 active:bg-gray-100 rounded-lg transition-colors"
                onClick={toggleMobileMenu}
                onTouchEnd={toggleMobileMenu}
                role="button"
                aria-label="メニュー"
                aria-expanded={isMobileMenuOpen}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl text-gray-900`}></i>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <div 
            className={`md:hidden bg-white border-t border-gray-200 shadow-lg transition-all duration-300 overflow-hidden ${
              isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-6 py-4 space-y-3">
              <div 
                onClick={() => handleMobileMenuClick('#home')}
                onTouchEnd={() => handleMobileMenuClick('#home')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                ホーム
              </div>
              <Link 
                to="/blog"
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
              >
                ブログ
              </Link>
              <div 
                onClick={() => handleMobileMenuClick('#properties')}
                onTouchEnd={() => handleMobileMenuClick('#properties')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                売却案件
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#financing')}
                onTouchEnd={() => handleMobileMenuClick('#financing')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                資金調達
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#investment')}
                onTouchEnd={() => handleMobileMenuClick('#investment')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer border-b border-gray-100"
                role="button"
              >
                事業案内
              </div>
              <div 
                onClick={() => handleMobileMenuClick('#contact')}
                onTouchEnd={() => handleMobileMenuClick('#contact')}
                className="block w-full text-left text-base font-medium text-gray-900 hover:text-teal-600 active:text-teal-700 transition-colors py-3 cursor-pointer"
                role="button"
              >
                お問い合わせ
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section id="home" className="relative pt-32 pb-20 bg-gradient-to-br from-teal-50 via-white to-teal-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                  系統用蓄電池事業希望者向け
                </div>
                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  <strong>系統用蓄電池</strong><br />
                  <span className="text-teal-600">事業のご案内</span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  安価な電力を<strong>蓄電池</strong>に貯蔵し、市場が必要とする時に高値で販売するビジネスモデル。<strong>東京電力管内</strong>に特化した戦略的<strong>蓄電池投資</strong>で、月間最大3,000万円以上の売上実績を誇ります。
                </p>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
                  <div className="flex items-start">
                    <i className="ri-file-list-line text-amber-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <p className="text-sm text-gray-700 font-semibold mb-1">事業候補地の売却情報</p>
                      <p className="text-sm text-gray-600">接続回答付案件買い取ります</p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <div className="flex items-start">
                    <i className="ri-funds-line text-blue-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <p className="text-sm text-gray-700 font-semibold mb-1">系統用蓄電池事業費調達サポート</p>
                    </div>
                  </div>
                </div>
                <div className="bg-teal-50 border-l-4 border-teal-500 p-4 mb-8">
                  <div className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <p className="text-sm text-gray-700 font-semibold mb-1">事業小口化のご案内</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="#properties" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer text-center">
                    売却案件を見る
                  </a>
                  <a href="#contact" className="bg-white hover:bg-gray-50 text-teal-600 px-8 py-4 rounded-lg text-sm font-semibold transition-all border-2 border-teal-600 whitespace-nowrap cursor-pointer text-center">
                    お問い合わせ
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-[500px] w-full rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://static.readdy.ai/image/429f3598ecc5059f29a02a26bec35937/0bf7e7a03cf35f4a0c3e87ffda7c7483.png"
                    alt="東京電力管内エリアマップ"
                    className="w-full h-full object-contain object-center bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Properties Section */}
        <section id="properties" className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"><strong>系統用蓄電池</strong>事業候補地案件の売却情報</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                <strong>東京電力管内</strong>の<strong>系統用蓄電池</strong>事業候補地をご紹介。土地・接続工事負担金を含む自社案件です。<strong>群馬県伊勢崎市</strong>、<strong>埼玉県</strong>、<strong>茨城県</strong>など、優良な<strong>蓄電池投資</strong>案件を多数取り扱っております。
              </p>
            </div>

            {/* Strategic Area Badge */}
            <div className="text-center mb-8">
              <div className="inline-block bg-teal-100 text-teal-700 px-6 py-3 rounded-full text-xl font-bold">
                東京電力管内
              </div>
            </div>

            {/* Quick Connect Properties */}
            <div className="bg-amber-50 rounded-2xl p-8 border-2 border-amber-200 mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-4"><strong>東京電力管内</strong>短期間接続案件</h3>
              <p className="text-gray-700 mb-6">発調契約申込済み。</p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* SF-68 Card */}
                <div className="bg-white rounded-xl shadow-lg p-6 border-4 border-amber-400">
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap mb-4 inline-block">
                    SF-68 ★注目★
                  </div>
                  <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-2">東京電力管内 茨�城県</div>
                    <div className="text-lg font-bold text-gray-900 mb-3">発調契約申込済み。</div>
                    <div className="text-sm text-gray-600 mb-2">所要工期</div>
                    <div className="text-xl font-bold text-red-600 mb-3 bg-yellow-100 p-3 rounded-lg border-2 border-red-600">
                      工事負担金入金後3ヶ月程度<br />(受給契約申込後6ヶ月)
                    </div>
                    <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded">回答月/2025年10月23日</div>
                  </div>
                </div>

                {/* Summary Card */}
                <div className="bg-yellow-100 rounded-xl shadow-lg p-6 border-2 border-yellow-400 flex flex-col justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-3">短期間接続案件残り8件あります</div>
                    <div className="text-lg font-bold text-amber-600 mb-4">2025年8月頃の運転開始予定 5件</div>
                    <div className="bg-amber-200 inline-block px-4 py-2 rounded-lg">
                      <p className="text-gray-900 font-semibold text-base">
                        資材の短期間調達が可能な企業様向け
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <a 
                  href="#contact"
                  className="inline-block bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  詳細を問い合わせる
                </a>
              </div>
            </div>

            {/* Standard Properties */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6"><strong>東京電力管内</strong> 6,000万円〜</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* SF-25 */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                        SF-25
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">東京電力管内 群馬県伊勢崎市</div>
                      <div className="text-base font-semibold text-gray-900 mb-2">発調契約申込済み。</div>
                      <div className="text-sm text-gray-700 mb-2">所要工期:工事負担金入金後7ヶ月程度<br />(受給契約申込後15ヶ月)</div>
                      <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded">回答月/2025年10月6日</div>
                    </div>
                    <a 
                      href="#contact"
                      className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                    >
                      詳細を問い合わせる
                    </a>
                  </div>
                </div>

                {/* SF-33 */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                        SF-33
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">東京電力管内 群馬県伊勢崎市</div>
                      <div className="text-base font-semibold text-gray-900 mb-2">発調契約申込済み。</div>
                      <div className="text-sm text-gray-700 mb-2">所要工期:工事負担金入金後7ヶ月程度<br />(受給契約申込後15ヶ月)</div>
                      <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded">回答月/2025年10月1日</div>
                    </div>
                    <a 
                      href="#contact"
                      className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                    >
                      詳細を問い合わせる
                    </a>
                  </div>
                </div>

                {/* BW-01 */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                        BW-01
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">東京電力管内 埼玉県</div>
                      <div className="text-base font-semibold text-gray-900 mb-2">発調契約申込済み。</div>
                      <div className="text-sm text-gray-700 mb-2">所要工期:工事負担金入金後12ヶ月程度<br />(受給契約申込後24ヶ月)</div>
                      <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded">回答月/2025年12月8日</div>
                    </div>
                    <a 
                      href="#contact"
                      className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                    >
                      詳細を問い合わせる
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-4">他にも多数の案件がございます</p>
                <a 
                  href="#contact"
                  className="inline-block bg-white hover:bg-gray-50 text-teal-600 px-8 py-4 rounded-lg text-sm font-semibold transition-all border-2 border-teal-600 whitespace-nowrap cursor-pointer"
                >
                  全案件リストを請求する
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Financing Section - 系統用蓄電池事業費調達サポート */}
        <section id="financing" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"><strong>系統用蓄電池</strong>事業費調達サポート</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                <strong>蓄電池事業</strong>開始に必要な資金調達をさまざまな方法でサポートします。<strong>東京電力管内</strong>での<strong>蓄電池投資</strong>を全面的にバックアップいたします。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* 設備資材リース型 */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-8 text-white">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                    <i className="ri-file-list-3-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">設備資材リース型</h3>
                  <p className="text-teal-50 text-sm">初期投資を抑えた事業展開が可能なリース方式。<br />事業資材の大規模調達から設置まで一括サポート。</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>初期費用の大幅削減</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>柔軟な支払いプラン</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>メンテナンス込み</span>
                    </li>
                  </ul>
                  <button className="mt-6 w-full bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer">
                    詳細を問い合わせる
                  </button>
                </div>
              </div>

              {/* 共同経営型 */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-8 text-white">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                    <i className="ri-group-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">共同経営型</h3>
                  <p className="text-blue-50 text-sm">事業者同士で出資し、再生可能エネルギー事業を展開。リスクを分散しながら事業規模拡大。</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-blue-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>リスクの分散</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-blue-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>専門知識の共有</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-blue-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>収益配分の柔軟性</span>
                    </li>
                  </ul>
                  <button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer">
                    詳細を問い合わせる
                  </button>
                </div>
              </div>

              {/* クラウドファンディング型 */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-8 text-white">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                    <i className="ri-funds-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">クラウドファンディング型</h3>
                  <p className="text-purple-50 text-sm">多数の投資家から資金を集め、大規模プロジェクトを実現。透明性の高い運用を提供。</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-purple-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>大規模資金調達</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-purple-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>透明性の確保</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <i className="ri-check-line text-purple-600 text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>広範な支援者獲得</span>
                    </li>
                  </ul>
                  <button className="mt-6 w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer">
                    詳細を問い合わせる
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Investment Section - 小口化のご案内 */}
        <section id="investment" className="py-24 bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"><strong>系統用蓄電池</strong>事業小口化投資のご案内</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                <strong>LLP（有限責任事業組合）</strong>を活用した共同投資モデル。少額から参加可能で、リスクを分散しながら安定収益を目指せます。<strong>東京電力管内</strong>での<strong>蓄電池投資</strong>を1,100万円から始められます。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <div className="relative h-[400px] w-full rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/c0157ec849734a68e250b7881411c67c.jpg"
                    alt="LLP投資モデル - 系統用蓄電池事業への共同投資"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6"><strong>LLP投資</strong>モデルの特徴</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-group-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">少額から参加可能</h4>
                      <p className="text-gray-600 text-sm">1,100万円～から参加できる共同投資モデル。大規模<strong>蓄電池投資</strong>のハードルを下げ、より多くの方に<strong>系統用蓄電池</strong>事業への投資機会を提供します。</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">有限責任（<strong>LLP</strong>）で安心</h4>
                      <p className="text-gray-600 text-sm">複数の投資家で<strong>蓄電池事業</strong>を共有することで、個人のリスクを軽減。有限責任により、出資額以上の損失は発生しません。</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-line-chart-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">安定した収益分配</h4>
                      <p className="text-gray-600 text-sm">市場収益から経費を控除後、持分割合に応じて定期的に収益を分配。<strong>東京電力管内</strong>の<strong>系統用蓄電池</strong>事業による透明性の高い運用体制で安心です。</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-settings-3-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">運用管理不要</h4>
                      <p className="text-gray-600 text-sm"><strong>蓄電池</strong>設備の保守・運用・市場取引はすべて専門業者が実施。投資家は収益を受け取るだけで、手間がかかりません。</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center"><strong>蓄電池投資</strong>シミュレーション例</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="text-center">
                  <div className="bg-teal-50 rounded-xl p-6 mb-4">
                    <div className="text-sm text-gray-600 mb-2">投資額</div>
                    <div className="text-3xl font-bold text-teal-600">1,100万円～</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-blue-50 rounded-xl p-6 mb-4">
                    <div className="text-sm text-gray-600 mb-2">想定年間利回り</div>
                    <div className="text-3xl font-bold text-blue-600">8〜12%</div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-information-line text-teal-600 text-xl mr-2"></i>
                  <strong>系統用蓄電池投資</strong>のポイント
                </h4>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                    <span>市場状況により収益は変動しますが、複数の市場（容量市場・卸電力市場・需給調整市場）から収益を得る構造により、<strong>系統用蓄電池</strong>事業の安定性を高めています。</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                    <span><strong>蓄電池</strong>設備には損害補償・休業補償に対応した保険が付帯し、メーカー製品保証も完備しています。</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                    <span><strong>蓄電池</strong>の寿命は約15〜20年。<strong>東京電力管内</strong>での長期的な視点での<strong>蓄電池投資</strong>をおすすめします。</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* LLP Explanation Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"><strong>LLP（有限責任事業組合）</strong>とは</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                参加する組合員が個性や能力を発揮しながら共同事業を行うことができる組織形態として、2005年8月1日に創設されました。<strong>系統用蓄電池</strong>事業への投資に最適な仕組みです。
              </p>
            </div>

            <div className="max-w-5xl mx-auto mb-16">
              <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 md:p-12">
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  同・異業種間や大・中・小規模企業間による連携、専門技能を持つ人材による連携など、さまざまな形態での共同事業を行うことが可能です。<strong>東京電力管内</strong>での<strong>蓄電池投資</strong>において、<strong>LLP</strong>は理想的な投資形態となります。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  また、地域活性や社会的貢献活動など、<strong>LLP</strong>の特性を活かすことによって、地域の独自性をはぐくみながら活動や事業を進める可能性が広がり、今までとは違った新しい展開が期待されます。<strong>系統用蓄電池</strong>事業を通じた地域貢献も実現できます。
                </p>
              </div>
            </div>

            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                共同で営利を目的とする事業を営むための組合組織であり、3つの特徴があります
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Feature 1 */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-8 text-white">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                      <i className="ri-shield-check-line text-3xl"></i>
                    </div>
                    <h4 className="text-2xl font-bold mb-2">1. 有限責任</h4>
                  </div>
                  <div className="p-6 h-32 flex items-center">
                    <p className="text-gray-700 leading-relaxed">
                      組合員は出資額の範囲までしか組合の債権者に対して責任を負いません。
                    </p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-8 text-white">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                      <i className="ri-settings-3-line text-3xl"></i>
                    </div>
                    <h4 className="text-2xl font-bold mb-2">2. 経営の柔軟性</h4>
                  </div>
                  <div className="p-6 h-32 flex items-center">
                    <p className="text-gray-700 leading-relaxed">
                      組合員間の損益分配や権限配分は、出資金額の比率とは異なる割合を決めることができます。
                    </p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-8 text-white">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                      <i className="ri-money-dollar-circle-line text-3xl"></i>
                    </div>
                    <h4 className="text-2xl font-bold mb-2">3. 構成員課税</h4>
                  </div>
                  <div className="p-6 h-32 flex items-center">
                    <p className="text-gray-700 leading-relaxed">
                      LLPで生じた損益は、LLP自体には課税されず、利益の分配を受けた組合員においてのみ課税されます。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">お問い合わせ</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  <strong>系統用蓄電池</strong>案件に関するご質問やご相談など、お気軽にお問い合わせください。<strong>東京電力管内</strong>での<strong>蓄電池投資</strong>について、専門スタッフが丁寧にご対応いたします。
                </p>
                
                <div className="space-y-6 mb-10">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-building-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1"><strong>株式会社新藤不動産</strong></h4>
                      <p className="text-gray-600 text-sm">代表取締役：新藤 誠</p>
                      <p className="text-gray-600 text-sm">宅地建物取引士</p>
                      <p className="text-gray-600 text-sm mt-1"><strong>系統用蓄電池</strong>事業専門</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">本社所在地</h4>
                      <p className="text-gray-600">〒372-0041<br /><strong>群馬県伊勢崎市</strong>平和町24-28</p>
                      <p className="text-sm text-gray-500 mt-1">（<strong>東京電力管内</strong>）</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">メールアドレス</h4>
                      <p className="text-gray-600">info.zaisan@xdesignsv.net</p>
                      <p className="text-sm text-gray-500 mt-1">受付時間:24時間</p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/shinjvmakc/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-teal-600 rounded-lg transition-colors group cursor-pointer">
                    <i className="ri-instagram-line text-gray-600 group-hover:text-white text-xl transition-colors"></i>
                  </a>
                  <a href="#" className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-teal-600 rounded-lg transition-colors group cursor-pointer">
                    <i className="ri-line-fill text-gray-600 group-hover:text-white text-xl transition-colors"></i>
                  </a>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <form data-readdy-form data-email-subject="【系統用蓄電池投資】お問い合わせ - 株式会社新藤不動産" id="contact-form" onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">お名前 <span className="text-red-600">*</span></label>
                      <input 
                        type="text" 
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                        placeholder="山田 太郎"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">メールアドレス <span className="text-red-600">*</span></label>
                      <input 
                        type="email" 
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                        placeholder="example@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="inquiry-type" className="block text-sm font-semibold text-gray-900 mb-2">お問い合わせ種別</label>
                    <select 
                      id="inquiry-type"
                      name="inquiry-type"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="property">事業候補地案件について</option>
                      <option value="quick-connect">短期間接続案件について</option>
                      <option value="financing">資金調達サポートについて</option>
                      <option value="investment">LLP投資について</option>
                      <option value="seminar">投資説明会に参加する</option>
                      <option value="other">その他</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">お問い合わせ内容 <span className="text-red-600">*</span></label>
                    <textarea 
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 resize-none text-sm"
                      placeholder="お問い合わせ内容をご記入ください"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">最大500文字</p>
                  </div>
                  <button 
                    type="submit"
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    送信する
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
              <div>
                <div className="mb-4">
                  <img 
                    src="https://static.readdy.ai/image/429f3598ecc5059f29a02a26bec35937/17c45356e81e22a47455156ff6042e28.png"
                    alt="株式会社新藤不動産"
                    className="h-12"
                  />
                  <p className="text-xl font-bold text-white mt-3">株式会社新藤不動産</p>
                  <p className="text-xl font-bold text-white">JV 株式会社マック</p>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  系統用蓄電池の投資事業を専門とする会社です。
                </p>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-4">メールマガジン</h4>
                <p className="text-gray-400 text-sm mb-4">最新案件情報をお届けします</p>
                <form data-readdy-form id="newsletter-form" onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2">
                  <input 
                    type="email" 
                    name="email"
                    required
                    placeholder="メールアドレス"
                    className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                  />
                  <button 
                    type="submit"
                    className="bg-teal-600 text-white hover:bg-teal-700 px-6 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    登録する
                  </button>
                </form>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">© 2024 株式会社新藤不動産. All rights reserved.</p>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </footer>

        {/* Popup Banner */}
        <PopupBanner />
      </div>
    </>
  );
}
